import { action, computed, makeObservable, observable } from 'mobx'

import { NotificationType } from '~/client/graph'
import { applyFacet, patchQuery } from '~/client/src/shared/lists/core'
import { ListStore } from '~/client/src/shared/lists/ListStore'
import type { NotificationAppliedFilterSets } from '~/client/src/shared/lists/notifications/notificationFilterVocabulary'
import {
  emptyNotificationAppliedFilters,
  isLegacyReadStatusId,
  NOTIFICATION_FILTER_FIELDS,
  prefixedNotificationFilterId,
  prefixedNotificationFilterIdsToFilterSets,
  toPrefixedNotificationFilterIds,
  toValuesForNotificationField,
} from '~/client/src/shared/lists/notifications/notificationFilterVocabulary'
import type BaseNotification from '~/client/src/shared/models/Notification'
import {
  isDeliveryType,
  isPermitType,
} from '~/client/src/shared/types/NotificationTypes'
import isUserBicForCurrentStepDefault from '~/client/src/shared/utils/isUserBicForCurrentStep'
import { SITE_COMM_TYPES } from '~/client/src/shared/utils/notificationItemHelpers'

import type {
  INotificationDelivery,
  INotificationRow,
  LegacyReadStatusId,
  NotificationSchemaDeps,
} from './notificationSchema'
import {
  createNotificationSchema,
  getAuthorName,
  LEGACY_READ_STATUS,
  MENTION_TYPES,
  notificationStatusIdForPermitStatus,
  notificationTypeLabel,
  NOTIFICATION_FIELD,
  NOTIFICATION_STATUS_MAP,
  NOTIFICATION_TYPE,
  PASS_THROUGH_OPTION,
  readStatusLabel,
  READ_STATUS,
  STATUS_LABELS,
} from './notificationSchema'

export enum NotificationTab {
  All = 'all',
  Important = 'important',
  Updates = 'updates',
  Archive = 'archive',
}

interface INotificationsRecordsStore {
  readonly latestForEachEntityFromActiveProject: readonly BaseNotification[]
}

export type NotificationsListStoreDeps = NotificationSchemaDeps & {
  readonly notificationsStore: INotificationsRecordsStore
}

interface SetTabOptions {
  readonly preserveSearch?: boolean
}

const DEFAULT_READ_STATUS_SELECTION: readonly LegacyReadStatusId[] = []
const IMPORTANT_READ_STATUS_SELECTION = [LEGACY_READ_STATUS.unread] as const
const ARCHIVE_READ_STATUS_SELECTION = [LEGACY_READ_STATUS.archived] as const

const IMPORTANT_APPLIED_FACETS = {
  [NOTIFICATION_FIELD.notificationType]: [
    NOTIFICATION_TYPE.bic,
    NOTIFICATION_TYPE.siteComm,
    NOTIFICATION_TYPE.mention,
  ],
  [NOTIFICATION_FIELD.readStatus]: mapReadStatusSelection(
    IMPORTANT_READ_STATUS_SELECTION,
  ),
}

const UPDATES_APPLIED_FACETS = {
  [NOTIFICATION_FIELD.notificationType]: [NOTIFICATION_TYPE.update],
  [NOTIFICATION_FIELD.readStatus]: mapReadStatusSelection(
    DEFAULT_READ_STATUS_SELECTION,
  ),
}

const ARCHIVE_APPLIED_FACETS = {
  [NOTIFICATION_FIELD.readStatus]: mapReadStatusSelection(
    ARCHIVE_READ_STATUS_SELECTION,
  ),
}

const DEFAULT_APPLIED_FACETS = {
  [NOTIFICATION_FIELD.readStatus]: mapReadStatusSelection(
    DEFAULT_READ_STATUS_SELECTION,
  ),
}

export class NotificationsListStore {
  public readonly list: ListStore<INotificationRow>
  @observable public activeTab: NotificationTab = NotificationTab.Important
  @observable private readStatusSelection: readonly LegacyReadStatusId[] =
    IMPORTANT_READ_STATUS_SELECTION

  private readonly deps: NotificationsListStoreDeps

  public constructor(deps: NotificationsListStoreDeps) {
    this.deps = deps
    this.list = new ListStore(createNotificationSchema(), () => this.rows, {
      initialQuery: {
        groupBy: [],
        appliedFacets: IMPORTANT_APPLIED_FACETS,
      },
    })

    makeObservable(this)
  }

  @action.bound
  public setTab(tab: NotificationTab, options: SetTabOptions = {}): void {
    this.activeTab = tab
    this.readStatusSelection = getTabReadStatusSelection(tab)
    const appliedFacets = getTabAppliedFacets(tab)
    this.list.dispatch(
      options.preserveSearch
        ? patchQuery({
            groupBy: [],
            appliedFacets,
          })
        : patchQuery({
            search: '',
            groupBy: [],
            appliedFacets,
          }),
    )
  }

  @action.bound
  public reset(): void {
    this.activeTab = NotificationTab.Important
    this.readStatusSelection = IMPORTANT_READ_STATUS_SELECTION
    this.list.reset({
      groupBy: [],
      appliedFacets: IMPORTANT_APPLIED_FACETS,
    })
  }

  @action.bound
  public setReadStatusSelection(ids: readonly LegacyReadStatusId[]): void {
    this.readStatusSelection = uniqueReadStatusSelection(ids)
    this.patchReadStatusFacet()
  }

  @action.bound
  public setDimensionFilter(
    fieldKey: string,
    optionIds: readonly string[],
  ): void {
    this.list.dispatch(
      applyFacet(
        fieldKey,
        optionIds.length ? [...optionIds, PASS_THROUGH_OPTION] : [],
      ),
    )
  }

  @computed
  public get appliedFilterSets(): NotificationAppliedFilterSets {
    const result = emptyNotificationAppliedFilters()

    for (const id of this.readStatusSelection) {
      result[NOTIFICATION_FIELD.readStatus].add(
        prefixedNotificationFilterId(NOTIFICATION_FIELD.readStatus, id),
      )
    }

    for (const field of NOTIFICATION_FILTER_FIELDS) {
      if (field.application === 'readStatus') continue
      const optionIds = this.list.query.appliedFacets[field.key] || []

      for (const optionId of optionIds) {
        if (optionId === PASS_THROUGH_OPTION) continue
        result[field.key].add(prefixedNotificationFilterId(field.key, optionId))
      }
    }

    return result
  }

  @computed
  public get prefixedFilterIds(): readonly string[] {
    return toPrefixedNotificationFilterIds(this.appliedFilterSets)
  }

  @action.bound
  public applyPrefixedFilterIds(prefixedIds: readonly string[]): void {
    this.applyFilterSets(prefixedNotificationFilterIdsToFilterSets(prefixedIds))
  }

  @action.bound
  public applyFilterSets(filtersToApply: NotificationAppliedFilterSets): void {
    const readStatusSelection = toValuesForNotificationField(
      filtersToApply,
      NOTIFICATION_FIELD.readStatus,
    ).filter(isLegacyReadStatusId)
    this.setReadStatusSelection(readStatusSelection)

    for (const field of NOTIFICATION_FILTER_FIELDS) {
      if (field.application === 'readStatus') continue
      const optionIds = toValuesForNotificationField(filtersToApply, field.key)

      if (field.application === 'dimension') {
        this.setDimensionFilter(field.key, optionIds)
      } else {
        this.setOptionFilter(field.key, optionIds)
      }
    }
  }

  @action.bound
  public toggleBicOnly(): void {
    const current =
      this.list.query.appliedFacets[NOTIFICATION_FIELD.notificationType] || []
    const next = current.includes(NOTIFICATION_TYPE.bic)
      ? current.filter(optionId => optionId !== NOTIFICATION_TYPE.bic)
      : [...current, NOTIFICATION_TYPE.bic]
    this.list.dispatch(
      patchQuery({
        appliedFacets: {
          ...this.list.query.appliedFacets,
          [NOTIFICATION_FIELD.notificationType]: next,
        },
      }),
    )
  }

  @action.bound
  public toggleUnreadOnly(): void {
    const unread = LEGACY_READ_STATUS.unread
    const next = this.isUnreadFilterActive
      ? this.readStatusSelection.filter(optionId => optionId !== unread)
      : [...this.readStatusSelection, unread]
    this.setReadStatusSelection(next)
  }

  @computed
  public get isBicFilterActive(): boolean {
    return (
      this.list.query.appliedFacets[
        NOTIFICATION_FIELD.notificationType
      ]?.includes(NOTIFICATION_TYPE.bic) || false
    )
  }

  @computed
  public get isUnreadFilterActive(): boolean {
    return this.readStatusSelection.includes(LEGACY_READ_STATUS.unread)
  }

  @computed
  public get isArchiveFilterActive(): boolean {
    return this.readStatusSelection.includes(LEGACY_READ_STATUS.archived)
  }

  @computed
  public get legacyReadStatusSelection(): readonly LegacyReadStatusId[] {
    return this.readStatusSelection
  }

  @action.bound
  public setOptionFilter(fieldKey: string, optionIds: readonly string[]): void {
    this.list.dispatch(applyFacet(fieldKey, optionIds))
  }

  @computed
  private get rows(): readonly INotificationRow[] {
    return this.deps.notificationsStore.latestForEachEntityFromActiveProject.map(
      notification => this.projectRow(notification),
    )
  }

  private patchReadStatusFacet(): void {
    this.list.dispatch(
      patchQuery({
        appliedFacets: {
          ...this.list.query.appliedFacets,
          [NOTIFICATION_FIELD.readStatus]: mapReadStatusSelection(
            this.readStatusSelection,
          ),
        },
      }),
    )
  }

  private projectRow(notification: BaseNotification): INotificationRow {
    const readStatusCell = getReadStatusCell(notification)
    const ntypeOptionIds = this.getNotificationTypeOptions(notification)
    const workflowType = this.getWorkflowType(notification)
    const workflowStatus = getWorkflowStatus(notification)
    const companyOptionIds = this.getCompanyOptions(notification)
    const locationOptionIds = this.getLocationOptions(notification)
    const equipmentOptionIds = this.getEquipmentOptions(notification)
    const editorOptionIds = getEditorOptions(notification)

    return {
      id: notification.id,
      notification,
      readStatusCell,
      readStatusLabel: readStatusLabel(readStatusCell),
      ntypeOptionIds,
      ntypeLabels: ntypeOptionIds.map(notificationTypeLabel),
      workflowType,
      workflowTypeLabel: workflowType
        ? this.getWorkflowTypeLabel(workflowType)
        : '',
      workflowStatus,
      workflowStatusLabel: workflowStatus
        ? STATUS_LABELS[workflowStatus] || workflowStatus
        : '',
      companyOptionIds,
      companyLabels: companyOptionIds.map(optionId =>
        this.getCompanyLabel(optionId),
      ),
      locationOptionIds,
      locationLabels: locationOptionIds.map(optionId =>
        this.getLocationLabel(optionId),
      ),
      equipmentOptionIds,
      equipmentLabels: equipmentOptionIds.map(optionId =>
        this.getEquipmentLabel(optionId),
      ),
      editorOptionIds,
      editorLabels: editorOptionIds.map(
        optionId =>
          getAuthorName(this.deps.projectMembersStore.getById(optionId)) ||
          optionId,
      ),
      searchBlob: this.getSearchBlob(notification),
    }
  }

  private getNotificationTypeOptions(notification: BaseNotification): string[] {
    const options: string[] = []
    if (MENTION_TYPES.includes(notification.type)) {
      options.push(NOTIFICATION_TYPE.mention)
    }
    if (this.isSiteCommunicationNotification(notification)) {
      options.push(NOTIFICATION_TYPE.siteComm)
    }
    if (this.isBicNotification(notification)) {
      options.push(NOTIFICATION_TYPE.bic)
    }
    if (this.isUpdateNotification(notification)) {
      options.push(NOTIFICATION_TYPE.update)
    }
    return options
  }

  private isSiteCommunicationNotification(
    notification: BaseNotification,
  ): boolean {
    if (SITE_COMM_TYPES.includes(notification.type)) return true
    if (isPermitType(notification.type) && notification.associatedPermit) {
      const permitType = this.deps.permitTypesStore?.getPermitTypeById(
        notification.associatedPermit.typeId,
      )
      return (
        permitType?.type === 'announcement' || permitType?.type === 'closure'
      )
    }
    return false
  }

  private isBicNotification(notification: BaseNotification): boolean {
    if (notification.type === NotificationType.NewUserCreatedViaSignUp) {
      return true
    }
    if (!isPermitType(notification.type)) return false
    const permit = notification.associatedPermit
    if (!permit) return false
    const userId = this.deps.getCurrentUserId()
    if (!userId) return false
    if (this.deps.isUserBicForCurrentStep) {
      return this.deps.isUserBicForCurrentStep(userId, permit)
    }
    return isUserBicForCurrentStepDefault(
      userId,
      permit as Parameters<typeof isUserBicForCurrentStepDefault>[1],
      this.deps.permitTypesStore as Parameters<
        typeof isUserBicForCurrentStepDefault
      >[2],
      this.deps.permitBicStore,
    )
  }

  private isUpdateNotification(notification: BaseNotification): boolean {
    if (SITE_COMM_TYPES.includes(notification.type)) return false
    if (isPermitType(notification.type) && notification.associatedPermit) {
      const permitType = this.deps.permitTypesStore?.getPermitTypeById(
        notification.associatedPermit.typeId,
      )
      if (
        permitType?.type === 'announcement' ||
        permitType?.type === 'closure'
      ) {
        return false
      }
    }

    return (
      isDeliveryType(notification.type) ||
      isPermitType(notification.type) ||
      notification.type === NotificationType.ActivityChanged ||
      notification.type === NotificationType.ScheduleUpdated
    )
  }

  private getWorkflowType(notification: BaseNotification): string | null {
    const typeId = notification.associatedPermit?.typeId
    if (!typeId) return null
    return this.deps.permitTypesStore?.getPermitTypeById(typeId)?.type || null
  }

  private getWorkflowTypeLabel(optionId: string): string {
    return (
      this.deps.permitTypesStore?.actualTypes?.find(
        type => type.type === optionId,
      )?.name || optionId
    )
  }

  private getCompanyLabel(optionId: string): string {
    return this.deps.companiesStore.getCompanyById(optionId)?.name || optionId
  }

  private getLocationLabel(optionId: string): string {
    return (
      this.deps.locationAttributesStore?.getById?.(optionId)?.name || optionId
    )
  }

  private getEquipmentLabel(optionId: string): string {
    return (
      this.deps.locationAttributesStore?.offloadingEquipmentsStore?.list?.find(
        equipment => equipment.id === optionId,
      )?.name || optionId
    )
  }

  private getCompanyOptions(notification: BaseNotification): readonly string[] {
    if (isDeliveryType(notification.type) && notification.entityId) {
      const delivery = this.getDelivery(notification)
      return delivery?.company ? [delivery.company] : []
    }
    if (notification.associatedPermit?.companyIds?.length) {
      return notification.associatedPermit.companyIds
    }
    return [PASS_THROUGH_OPTION]
  }

  private getLocationOptions(
    notification: BaseNotification,
  ): readonly string[] {
    if (isDeliveryType(notification.type) && notification.entityId) {
      const delivery = this.getDelivery(notification)
      if (!delivery) return []
      const locationId = getDeliveryLocationId(delivery)
      return locationId ? [locationId] : []
    }
    if (notification.associatedPermit) {
      return (notification.associatedPermit.locations || []).flatMap(
        location => (location.id ? [location.id] : []),
      )
    }
    return [PASS_THROUGH_OPTION]
  }

  private getEquipmentOptions(
    notification: BaseNotification,
  ): readonly string[] {
    if (isDeliveryType(notification.type) && notification.entityId) {
      const delivery = this.getDelivery(notification)
      if (delivery) return delivery.offloadingEquipmentIds || []
    }
    return [PASS_THROUGH_OPTION]
  }

  private getSearchBlob(notification: BaseNotification): string {
    const parts: string[] = []
    if (isDeliveryType(notification.type) && notification.entityId) {
      const delivery = this.getDelivery(notification)
      if (delivery?.bookingId) parts.push(delivery.bookingId)
      if (delivery?.company) {
        const company = this.deps.companiesStore.getCompanyById(
          delivery.company,
        )
        if (company?.name) parts.push(company.name)
      }
    }
    if (isPermitType(notification.type) && notification.associatedPermit) {
      if (notification.associatedPermit.code) {
        parts.push(notification.associatedPermit.code)
      }
      if (notification.associatedPermit.title) {
        parts.push(notification.associatedPermit.title)
      }
    }
    if (notification.authorId) {
      const author = this.deps.projectMembersStore.getById(
        notification.authorId,
      )
      if (author) parts.push(getAuthorName(author))
    }
    return parts.join(' ').toLowerCase()
  }

  private getDelivery(
    notification: BaseNotification,
  ): INotificationDelivery | undefined {
    return notification.entityId
      ? this.deps.deliveriesStore.byId.get(notification.entityId)
      : undefined
  }
}

export function mapReadStatusSelection(
  selected: readonly LegacyReadStatusId[],
): readonly string[] {
  const hasArchived = selected.includes(LEGACY_READ_STATUS.archived)
  const hasRead = selected.includes(LEGACY_READ_STATUS.read)
  const hasUnread = selected.includes(LEGACY_READ_STATUS.unread)

  if (hasArchived && !hasRead && !hasUnread) {
    return [READ_STATUS.archivedUnread, READ_STATUS.archivedRead]
  }
  if (!hasRead && !hasUnread) return [READ_STATUS.unread, READ_STATUS.read]
  if (hasRead && hasUnread) {
    return hasArchived
      ? [
          READ_STATUS.unread,
          READ_STATUS.read,
          READ_STATUS.archivedUnread,
          READ_STATUS.archivedRead,
        ]
      : [READ_STATUS.unread, READ_STATUS.read]
  }
  if (hasUnread) {
    return hasArchived
      ? [READ_STATUS.unread, READ_STATUS.archivedUnread]
      : [READ_STATUS.unread]
  }
  if (hasRead) {
    return hasArchived
      ? [READ_STATUS.read, READ_STATUS.archivedRead]
      : [READ_STATUS.read]
  }
  return [READ_STATUS.unread, READ_STATUS.read]
}

function getTabAppliedFacets(
  tab: NotificationTab,
): Readonly<Record<string, readonly string[]>> {
  switch (tab) {
    case NotificationTab.Important:
      return IMPORTANT_APPLIED_FACETS
    case NotificationTab.Updates:
      return UPDATES_APPLIED_FACETS
    case NotificationTab.Archive:
      return ARCHIVE_APPLIED_FACETS
    case NotificationTab.All:
    default:
      return DEFAULT_APPLIED_FACETS
  }
}

function getTabReadStatusSelection(
  tab: NotificationTab,
): readonly LegacyReadStatusId[] {
  switch (tab) {
    case NotificationTab.Important:
      return IMPORTANT_READ_STATUS_SELECTION
    case NotificationTab.Archive:
      return ARCHIVE_READ_STATUS_SELECTION
    case NotificationTab.Updates:
    case NotificationTab.All:
    default:
      return DEFAULT_READ_STATUS_SELECTION
  }
}

function uniqueReadStatusSelection(
  ids: readonly LegacyReadStatusId[],
): readonly LegacyReadStatusId[] {
  const allowed = Object.values(LEGACY_READ_STATUS)
  return allowed.filter(id => ids.includes(id))
}

function getReadStatusCell(notification: BaseNotification): string {
  if (notification.isArchived) {
    return notification.wasRead
      ? READ_STATUS.archivedRead
      : READ_STATUS.archivedUnread
  }

  return notification.wasRead ? READ_STATUS.read : READ_STATUS.unread
}

function getWorkflowStatus(notification: BaseNotification): string | null {
  if (
    (isPermitType(notification.type) ||
      notification.type === NotificationType.PermitMessageReceived) &&
    notification.associatedPermit?.status
  ) {
    return notificationStatusIdForPermitStatus(
      notification.associatedPermit.status,
    )
  }
  return NOTIFICATION_STATUS_MAP[notification.type] ?? null
}

function getEditorOptions(notification: BaseNotification): readonly string[] {
  return notification.authorId ? [notification.authorId] : [PASS_THROUGH_OPTION]
}

function getDeliveryLocationId(
  delivery: INotificationDelivery,
): string | undefined {
  return (
    delivery.building ||
    delivery.zone ||
    delivery.gate ||
    delivery.route ||
    delivery.level ||
    delivery.area
  )
}
